import { defineStore, acceptHMRUpdate } from 'pinia';

import type { MediaSchema } from '~/types/schema';

import { getServices } from '~/db/services';

import { mapRequestProfile } from '~/lib/utils/dataMapping';

export const useProfileStore = defineStore('ProfileStore', () => {
  if (!useAppRunningStore().isRunning) return;

  const { $db } = useNuxtApp();

  const { profilesService, mediasService } = getServices($db);

  const { isMyFutureEmpty } = useMyFutureEmptySyncGet();

  const { $sentrySetTag } = useNuxtApp();
  const data = ref<Profile>(null);
  const mediaAvatar = ref<MediaSchema>();
  const { profile: profileDataSync } = useProfileSyncGet();

  const config = useConfigStore();

  const assessmentsStore = useAssessmentsStore();

  const userDebug = useNomadStorage('user', 'common', 'system');

  const newChildrenSharingData = ref<FamilyMember[]>([]);

  const hasNewChildSharingData = computed(() => {
    return newChildrenSharingData.value.length > 0;
  });

  const shiftNewChildrenSharingData = (): FamilyMember => {
    return newChildrenSharingData.value.shift();
  };

  const enqueueNewChildrenSharingData = (member: FamilyMember) => {
    return newChildrenSharingData.value.push(member);
  };

  const isJustJoiningNewFamilyNomadPlus = ref<boolean>(false);

  const setIsJustJoiningNewFamilyNomadPlus = (b: boolean) => {
    // set with action useFamilyNewMemberNomad
    isJustJoiningNewFamilyNomadPlus.value = b;
  };

  watchEffect(() => {
    if (profileDataSync.value && profileDataSync.value.length > 0) {
      forceUpdateRawValue(profileDataSync.value[0].data);
    } else {
      data.value = null;
    }
  });

  const forceUpdateRawValue = (rawValue) => {
    data.value = mapRequestProfile(rawValue);
  };

  const loadFromClient = async (): Promise<boolean> => {
    const context = useContextStore();
    const profileData = await profilesService.get({
      id: context.memberId,
    });
    if (profileData) {
      data.value = mapRequestProfile(profileData.data);
      return true;
    }
    return false;
  };

  const contents = computed<UserContent[]>(() => {
    if (!data.value) return [];
    return data.value.userContent.map((content, index) => {
      return {
        ...content,
        index,
      };
    });
  });

  const isStripe = computed(() => {
    if (!data.value) return null;
    return data.value.stripeCustomerId;
  });

  const mainContent = computed(() =>
    contents.value.find(
      (content) => content.type === UserContentType.MainContent
    )
  );

  const libraryBookIds = computed(() => {
    return contents.value.map((content) => content.id);
  });

  const isASubscriber = computed<boolean>(() => {
    if (!data.value) return false;
    return data.value.plans != null && data.value.plans.length > 0;
  });

  const currentDegree = computed<string>(() => {
    if (!data.value) return null;
    return data.value.currentDegree;
  });

  const branch = computed<string>(() => {
    if (!data.value) return null;
    return data.value.branch;
  });

  const products = computed<string>(() => {
    if (!data.value) return null;
    return data.value.products;
  });

  const plans = computed<string>(() => {
    if (!data.value) return null;
    return data.value.plans;
  });

  const plansId = computed<string>(() => {
    if (!data.value) return null;
    return data.value.plansId;
  });

  const avatar = computed(() => {
    if (!data.value) return null;
    return data.value.avatar;
  });

  const accessType = computed(() => {
    if (!data.value) return null;
    return data.value.accessType;
  });

  const hasAFamily = computed(() => {
    if (!data.value) return false;
    return data.value.family !== null && data.value.family?.familyCode != null;
  });

  const family = computed<FamilyType>(() => {
    if (!data.value) return null;

    return data.value.family;
  });

  const familyMembers = computed(() => {
    if (!family.value) return [];
    return family.value?.members || [];
  });

  const children = computed(() => {
    return familyMembers.value.filter((member) => member.status !== 'parent');
  });

  const isParent = computed(() => {
    if (!data.value) return false;
    return accessType.value === 'parent';
  });

  const maxFamilyMembersCount = computed(() => {
    if (!family.value) return 5;
    return family.value.maxFamilyMembersCount ?? 5;
  });

  const isAllowedToAddFamilyMember = computed<boolean>(() => {
    return familyMembers.value.length < maxFamilyMembersCount.value;
  });

  const memberPreferences = computed<any>(() => {
    if (!data.value) return {};
    return data.value.memberPreferences;
  });

  const textDyslexicMode = computed(() => {
    return memberPreferences.value.textDyslexicMode;
  });

  const shareResultsWithFamily = computed(() => {
    return memberPreferences.value.shareResultsWithFamily;
  });

  const isAvatarSelected = computed(() => {
    return avatar.value != null;
  });

  const getAvatar = async (): Promise<MediaSchema> => {
    if (!avatar.value) return null;
    if (mediaAvatar.value && mediaAvatar.value?.id) {
      if (mediaAvatar.value.id === avatar.value) return;
    }
    mediaAvatar.value = await mediasService.get({ id: avatar.value });
  };

  const adaptiveParameters = computed(() => {
    if (!data?.value?.adaptiveParameters) return null;
    return data?.value?.adaptiveParameters;
  });

  const adaptiveModel = computed(() => {
    if (!adaptiveParameters.value) return null;
    return {
      version: adaptiveParameters.value.version,
      parameters: { ...adaptiveParameters.value.parameters },
    };
  });

  const displayName = computed(() => {
    return data.value?.displayName;
  });

  const loginToken = computed(() => {
    return data.value?.loginToken;
  });

  const levelOfEducation = computed(() => data.value?.levelOfEducation ?? '');

  const levelOfEducationId = computed(
    () => data.value?.levelOfEducationId ?? ''
  );

  const coachingPlan = computed<ProfileCoachingPlan>(() => {
    const coachingPlan = data.value?.coachingPlan || {};
    return {
      realAppId: null,
      dailyMinutesForActive: 0,
      daysPerWeek: 0,
      minutesPerWeek: 0,
      objectiveValue: '',
      id: '',
      ...coachingPlan,
    };
  });

  const featureFlags = computed<ProfileFeatureFlags>(() => {
    const featureFlags = data.value?.featureFlags || {};
    return {
      useFormal: false,
      displayEmail: false,
      avatar: true,
      displayName: true,
      adaptive: false,
      gradeSimulator: true,
      feed: false,
      home: true,
      forYou: true,
      myFuture: true,
      friend: false,
      updateProfiling: false,
      ...featureFlags,
    };
  });

  const useFormal = computed(() => featureFlags?.value?.useFormal ?? false);

  const isAdaptiveEnabled = computed(() => {
    if (!featureFlags.value.adaptive) return false;
    return data.value?.adaptiveProfile?.isEnabled ?? false;
  });

  const isFriendEnabled = computed(() => {
    return featureFlags.value?.friend || false;
  });

  const isMyFutureEnabled = computed(() => {
    return featureFlags.value?.myFuture || false;
  });

  const refreshProfiling = computed<RefreshProfiling>(() => {
    return data.value?.refreshProfiling ?? null;
  });

  const zip = computed(() => {
    return data.value?.zip ?? null;
  });

  const phone = computed(() => {
    return data.value?.phone ?? null;
  });

  const syncContext = computed<SyncContext>(() => {
    return {
      isAdaptiveEnabled: isAdaptiveEnabled.value,
      isFriendEnabled: isFriendEnabled.value,
      isMyFutureEnabled: isMyFutureEnabled.value,
    };
  });

  const schoolContactAcceptance = computed(() => {
    return data.value?.schoolContactAcceptance ?? false;
  });

  const needToRefreshProfiling = computed(() => {
    if (!refreshProfiling.value) return false;
    const step = refreshProfiling.value?.step ?? 'none';
    if (step !== 'none') return true;
    return false;
  });

  const needToDefineMainContent = computed(() => {
    return (
      contents.value.length === 0 ||
      !contents.value.find(
        (content) => content.type === UserContentType.MainContent
      )
    );
  });

  const displayNameAndAvatarUnset = computed(() => {
    return data.value?.displayNameAndAvatarUnset || false;
  });

  const lockedContent = computed(() => {
    const lockedContent = data.value?.lockedContent || {};
    return {
      practice: true,
      annals: true,
      contentPDF: true,
      dimensionTest: true,
      essential: true,
      post: false,
      practiceCorrectionPDF: true,
      practiceSubjectPDF: true,
      practiceToolkitPDF: true,
      quiz: false,
      course: false,
      course_video: false,
      result: true,
      ...lockedContent,
    };
  });

  const lockedContentForUser = computed(() => {
    if (isASubscriber.value) {
      return {
        annals: false,
        practice: false,
        contentPDF: false,
        dimensionTest: false,
        essential: false,
        post: false,
        practiceCorrectionPDF: false,
        practiceSubjectPDF: false,
        practiceToolkitPDF: false,
        quiz: false,
        result: false,
      };
    }
    const lockedContent = data.value?.lockedContent || {};
    return {
      practice: true,
      annals: true,
      contentPDF: true,
      dimensionTest: true,
      essential: true,
      post: false,
      practiceCorrectionPDF: true,
      practiceSubjectPDF: true,
      practiceToolkitPDF: true,
      // quiz: false,
      result: true,
      ...lockedContent,
    };
  });

  const displayEmail = computed(
    () => featureFlags?.value?.displayEmail ?? false
  );

  const emailDeliverable = computed(
    () => data.value?.emailDeliverable ?? false
  );

  const fields = computed(() => {
    return data.value?.fields ?? [];
  });

  const canCreateNewAssessment = computed(() => {
    if (isASubscriber.value) return true;
    return (
      assessmentsStore.assessments.length < config.maxUnsubscribedAssessments
    );
  });

  const testMember = computed(() => {
    const localMemberTest = userDebug.value === 'testMember'; // set with plugin konami
    return data.value?.testMember ?? localMemberTest;
  });

  const actionPopups = computed(() => {
    const actionPopups = data.value?.actionPopups || {};
    return {
      exerciseResults: {
        enabled: true,
        value: '',
      },
      ...actionPopups,
    };
  });

  const parentInfo = computed(() => {
    const parentInfo = data.value?.parentInfo || {};
    return {
      email: '',
      ...parentInfo,
    };
  });

  const actionRequest = computed(() => {
    const actionRequest = data.value?.actionRequest || null;
    return actionRequest;
    // MOCK ::
    // return JSON.parse(
    //   `{"mediaId":"685d558f5b0f8009b47b0aad","title":"Peux-tu confirmer que cette année tu es bien en :","mandatory":true,"bulletList":[{"label":"Niveau d'éducation","value":"Terminale (2024-2025)"},{"label":"Diplôme en cours","value":"Bac général et technologique"},{"label":"Cursus","value":"Bac Général"},{"label":"Spécialité (Nouveau Bac)","value":"Mathématiques, Physique-Chimie"},{"label":"Enseignement optionnel","value":"Mathématiques expertes"},{"label":"Langue(s)","value":"Anglais"}],"actions":[{"label":"Oui","highlighted":true,"adamActionParameters":{"method":"confirm_b2s","params":{"backToSchoolConfirmed":true}}},{"label":"Non","highlighted":false,"adamActionParameters":{"method":"confirm_b2s","params":{"backToSchoolConfirmed":false}}}]}`
    // );
  });

  watchEffect(async () => {
    if (!avatar.value) return null;
    await getAvatar();
  });

  watchEffect(() => {
    if (accessType.value) {
      $sentrySetTag('access.type', accessType.value);
    }
  });

  watchEffect(() => {
    if (libraryBookIds.value) {
      $sentrySetTag('content.count', libraryBookIds.value.length);
    }
  });

  watchEffect(() => {
    if (isASubscriber.value) {
      $sentrySetTag('subscriber', isASubscriber.value ? 'yes' : 'no');
    }
  });

  // Watch members props to show dialogs :
  // - dialog ajout nouvelle famille
  // - dialog a partager ses infos
  useProfileFamilyWatcher();

  return {
    data: data as Readonly<Ref<Profile>>,
    maxFamilyMembersCount,
    forceUpdateRawValue,
    loadFromClient,
    getAvatar,
    actionPopups,
    mediaAvatar,
    isAvatarSelected,
    avatar,
    contents,
    mainContent,
    libraryBookIds,
    isASubscriber,
    displayName,
    adaptiveModel,
    adaptiveParameters,
    levelOfEducation,
    levelOfEducationId,
    featureFlags,
    isAdaptiveEnabled,
    needToRefreshProfiling,
    refreshProfiling,
    needToDefineMainContent,
    useFormal,
    lockedContent,
    lockedContentForUser,
    family,
    hasAFamily,
    isAllowedToAddFamilyMember,
    accessType,
    familyMembers,
    children,
    isParent,
    memberPreferences,
    textDyslexicMode,
    shareResultsWithFamily,
    displayEmail,
    fields,
    plans,
    plansId,
    products,
    currentDegree,
    branch,
    isStripe,
    canCreateNewAssessment,
    testMember,
    coachingPlan,
    displayNameAndAvatarUnset,
    parentInfo,
    loginToken,
    isFriendEnabled,
    isMyFutureEnabled,
    syncContext,
    schoolContactAcceptance,
    zip,
    phone,
    actionRequest,
    emailDeliverable,
    isMyFutureEmpty,
    isJustJoiningNewFamilyNomadPlus,
    setIsJustJoiningNewFamilyNomadPlus,
    hasNewChildSharingData,
    shiftNewChildrenSharingData,
    enqueueNewChildrenSharingData,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProfileStore, import.meta.hot));
}
